import { getLang } from '@/shared/i18n';

/**
 * Языки перевода — тот же список, что App\Shared\I18n\Languages на сервере.
 * Название по-русски («английский»), откуда перевели («с английского») и по-английски для английского интерфейса.
 */
const LANGUAGES = {
  ru: ['русский', 'русского', 'Russian'],
  en: ['английский', 'английского', 'English'],
  uk: ['украинский', 'украинского', 'Ukrainian'],
  be: ['белорусский', 'белорусского', 'Belarusian'],
  kk: ['казахский', 'казахского', 'Kazakh'],
  uz: ['узбекский', 'узбекского', 'Uzbek'],
  hy: ['армянский', 'армянского', 'Armenian'],
  ka: ['грузинский', 'грузинского', 'Georgian'],
  az: ['азербайджанский', 'азербайджанского', 'Azerbaijani'],
  de: ['немецкий', 'немецкого', 'German'],
  fr: ['французский', 'французского', 'French'],
  es: ['испанский', 'испанского', 'Spanish'],
  it: ['итальянский', 'итальянского', 'Italian'],
  pt: ['португальский', 'португальского', 'Portuguese'],
  nl: ['нидерландский', 'нидерландского', 'Dutch'],
  pl: ['польский', 'польского', 'Polish'],
  cs: ['чешский', 'чешского', 'Czech'],
  sr: ['сербский', 'сербского', 'Serbian'],
  el: ['греческий', 'греческого', 'Greek'],
  tr: ['турецкий', 'турецкого', 'Turkish'],
  fi: ['финский', 'финского', 'Finnish'],
  sv: ['шведский', 'шведского', 'Swedish'],
  ar: ['арабский', 'арабского', 'Arabic'],
  he: ['иврит', 'иврита', 'Hebrew'],
  hi: ['хинди', 'хинди', 'Hindi'],
  zh: ['китайский', 'китайского', 'Chinese'],
  ja: ['японский', 'японского', 'Japanese'],
  ko: ['корейский', 'корейского', 'Korean'],
  th: ['тайский', 'тайского', 'Thai'],
  vi: ['вьетнамский', 'вьетнамского', 'Vietnamese'],
  id: ['индонезийский', 'индонезийского', 'Indonesian'],
} as const satisfies Record<string, readonly [string, string, string]>;

export type LanguageCode = keyof typeof LANGUAGES;

export const LANGUAGE_CODES: readonly string[] = Object.keys(LANGUAGES);

export function isLanguage(code: string): code is LanguageCode {
  return Object.hasOwn(LANGUAGES, code);
}

/** Коды через пробел: известные и без повторов (настройка «Не переводить»). */
export function validLangs(value: string): boolean {
  if (value === '') return true;
  const codes = value.split(' ');
  return codes.every(isLanguage) && new Set(codes).size === codes.length;
}

export function parseLangs(value: string): LanguageCode[] {
  return value === '' ? [] : value.split(' ').filter(isLanguage);
}

/** «английский» — для «Перевести на английский»; в английском интерфейсе — «English». */
export function languageName(code: string): string {
  if (!isLanguage(code)) return code;
  return getLang() === 'en' ? LANGUAGES[code][2] : LANGUAGES[code][0];
}

/** «английского» — для «переведено с английского». */
export function languageFrom(code: string): string {
  if (!isLanguage(code)) return code;
  return getLang() === 'en' ? LANGUAGES[code][2] : LANGUAGES[code][1];
}

/** «Английский» — для списков. */
export function languageLabel(code: string): string {
  const name = languageName(code);
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/** Пункты списка: сначала язык интерфейса и английский, остальные — по алфавиту. */
export function languageOptions(): { value: LanguageCode; label: string }[] {
  const first: LanguageCode[] = getLang() === 'en' ? ['en', 'ru'] : ['ru', 'en'];
  const rest = LANGUAGE_CODES.filter(isLanguage).filter((c) => !first.includes(c)).sort((a, b) => languageLabel(a).localeCompare(languageLabel(b), getLang()));
  return [...first, ...rest].map((value) => ({ value, label: languageLabel(value) }));
}

/** На какой язык переводить: из настройки, иначе — язык интерфейса. */
export function targetLanguage(setting: string): LanguageCode {
  return isLanguage(setting) ? setting : getLang();
}
